import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Loader2, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { LeaseTermsDialog } from '../components/leasing/LeaseTermsDialog';
import { RenewalHistoryDialog } from '../components/leasing/RenewalHistoryDialog';
import { EndLeaseDialog, ScheduledTermination, TerminatedOn } from '../components/leasing/EndLeaseDialog';
import { useLanguage } from '../context/LanguageContext';

const EMPTY = { client_company_id: '', license_plate: '', make: '', model: '', year: new Date().getFullYear(), vin: '', current_mileage: 0, contract_end_date: '', km_limit: '' };

const Field = ({ label, children }) => (
    <div className="space-y-1"><Label className="text-slate-700 text-sm">{label}</Label>{children}</div>
);

const AddLeasedVehicleForm = ({ clients, el, onCreated }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.client_company_id || !form.license_plate.trim() || !form.make.trim() || !form.model.trim()) {
            toast.error(el ? 'Συμπληρώστε μισθωτή, πινακίδα, μάρκα και μοντέλο' : 'Fill in lessee, plate, make and model');
            return;
        }
        setSaving(true);
        try {
            await api.post('/leasing/vehicles', { ...form, year: Number(form.year), current_mileage: Number(form.current_mileage) || 0, contract_end_date: form.contract_end_date || null, km_limit: form.km_limit ? Number(form.km_limit) : null });
            toast.success(el ? 'Το όχημα ανατέθηκε - αναμένει επιβεβαίωση από τον μισθωτή' : 'Vehicle assigned - awaiting lessee confirmation');
            setForm(EMPTY);
            onCreated();
        } catch (error) {
            toast.error(error.response?.data?.detail || (el ? 'Αποτυχία' : 'Failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="fleet-card p-5 space-y-4" data-testid="leasing-vehicle-form">
            <h2 className="text-base md:text-lg font-semibold text-slate-800">{el ? 'Νέο όχημα leasing' : 'New leasing vehicle'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label={el ? 'Μισθωτής' : 'Lessee'}>
                    <Select value={form.client_company_id} onValueChange={(v) => setForm({ ...form, client_company_id: v })}>
                        <SelectTrigger className="bg-white border-slate-300" data-testid="leasing-vehicle-client-select"><SelectValue placeholder={el ? 'Επιλέξτε μισθωτή' : 'Select lessee'} /></SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label={el ? 'Πινακίδα' : 'License plate'}><Input value={form.license_plate} onChange={set('license_plate')} className="bg-white border-slate-300" data-testid="leasing-vehicle-plate" /></Field>
                <Field label="VIN"><Input value={form.vin} onChange={set('vin')} className="bg-white border-slate-300" data-testid="leasing-vehicle-vin" /></Field>
                <Field label={el ? 'Μάρκα' : 'Make'}><Input value={form.make} onChange={set('make')} className="bg-white border-slate-300" data-testid="leasing-vehicle-make" /></Field>
                <Field label={el ? 'Μοντέλο' : 'Model'}><Input value={form.model} onChange={set('model')} className="bg-white border-slate-300" data-testid="leasing-vehicle-model" /></Field>
                <Field label={el ? 'Έτος' : 'Year'}><Input type="number" value={form.year} onChange={set('year')} className="bg-white border-slate-300" data-testid="leasing-vehicle-year" /></Field>
                <Field label={el ? 'Χιλιόμετρα' : 'Odometer (km)'}><Input type="number" value={form.current_mileage} onChange={set('current_mileage')} className="bg-white border-slate-300" data-testid="leasing-vehicle-mileage" /></Field>
                <Field label={el ? 'Λήξη σύμβασης' : 'Contract end date'}><Input type="date" value={form.contract_end_date} onChange={set('contract_end_date')} className="bg-white border-slate-300" data-testid="leasing-vehicle-end-date" /></Field>
                <Field label={el ? 'Όριο χιλιομέτρων' : 'Km limit'}><Input type="number" value={form.km_limit} onChange={set('km_limit')} className="bg-white border-slate-300" data-testid="leasing-vehicle-km-limit" /></Field>
            </div>
            <Button type="submit" disabled={saving || !clients.length} className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="leasing-vehicle-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" />{el ? 'Ανάθεση σε μισθωτή' : 'Assign to lessee'}</>}
            </Button>
            {!clients.length && <p className="text-xs text-amber-600">{el ? 'Δεν υπάρχουν ακόμα συνδεδεμένοι μισθωτές.' : 'No linked lessees yet.'}</p>}
        </form>
    );
};

const StatusBadge = ({ status, el }) => (status === 'terminated'
    ? <span className="inline-flex items-center gap-1 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">{el ? 'Τερματισμένο' : 'Ended'}</span>
    : status === 'pending'
    ? <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded"><Clock className="w-3 h-3" />{el ? 'Σε αναμονή' : 'Pending'}</span>
    : <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded"><CheckCircle2 className="w-3 h-3" />{el ? 'Επιβεβαιωμένο' : 'Confirmed'}</span>);

export default function LeasingVehiclesPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const [clients, setClients] = useState([]);
    const [vehicles, setVehicles] = useState([]);

    const load = useCallback(async () => {
        const [c, v] = await Promise.all([api.get('/leasing/clients'), api.get('/leasing/vehicles')]);
        setClients(c.data);
        setVehicles(v.data);
    }, []);

    useEffect(() => { load().catch(() => {}); }, [load]);

    const remove = async (id) => {
        try {
            await api.delete(`/leasing/vehicles/${id}`);
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        }
    };

    return (
        <div className="space-y-6" data-testid="leasing-vehicles-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">{el ? 'Οχήματα Leasing' : 'Leasing Vehicles'}</h1>
                <p className="text-zinc-500">{vehicles.length} {el ? 'καταχωρήσεις' : 'entries'}</p>
            </div>
            <AddLeasedVehicleForm clients={clients} el={el} onCreated={load} />
            <div className="fleet-card p-5 overflow-x-auto">
                <table className="w-full text-sm" data-testid="leasing-vehicles-table">
                    <thead>
                        <tr className="text-left text-xs text-slate-500">
                            <th className="pb-2 pr-3">{el ? 'Πινακίδα' : 'Plate'}</th>
                            <th className="pb-2 pr-3">{el ? 'Όχημα' : 'Vehicle'}</th>
                            <th className="pb-2 pr-3">{el ? 'Μισθωτής' : 'Lessee'}</th>
                            <th className="pb-2 pr-3">{el ? 'Κατάσταση' : 'Status'}</th>
                            <th className="pb-2 pr-3">km</th>
                            <th className="pb-2 pr-3">{el ? 'Όριο km' : 'Km limit'}</th>
                            <th className="pb-2 pr-3">{el ? 'Λήξη σύμβασης' : 'Contract end'}</th>
                            <th className="pb-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {vehicles.map((v) => (
                            <tr key={v.id} className={`border-t border-slate-100 ${v.status === 'terminated' ? 'opacity-60' : ''}`} data-testid={`leasing-vehicle-row-${v.id}`}>
                                <td className="py-2 pr-3 font-medium text-slate-800">{v.license_plate}</td>
                                <td className="py-2 pr-3 text-slate-600">{v.make} {v.model} {v.year}</td>
                                <td className="py-2 pr-3 text-slate-600">{v.client_company_name}</td>
                                <td className="py-2 pr-3 space-x-1"><StatusBadge status={v.status} el={el} />{v.status === 'confirmed' && v.termination_date && <ScheduledTermination vehicle={v} el={el} onChanged={load} />}{v.status === 'terminated' && <TerminatedOn vehicle={v} el={el} />}</td>
                                <td className="py-2 pr-3 text-slate-700">{(v.current_mileage || 0).toLocaleString(el ? 'el-GR' : 'en-GB')}</td>
                                <td className={`py-2 pr-3 ${v.alerts?.some((a) => a.type === 'km_limit') ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>{v.km_limit ? v.km_limit.toLocaleString(el ? 'el-GR' : 'en-GB') : '-'}</td>
                                <td className={`py-2 pr-3 ${v.alerts?.some((a) => a.type === 'contract_end') ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>{v.contract_end_date ? new Date(v.contract_end_date).toLocaleDateString(el ? 'el-GR' : 'en-GB') : '-'}</td>
                                <td className="py-2 text-right whitespace-nowrap">
                                    <RenewalHistoryDialog vehicle={v} el={el} />
                                    {v.status !== 'terminated' && <LeaseTermsDialog vehicle={v} el={el} onSaved={load} />}
                                    {v.status === 'confirmed' && !v.termination_date && <EndLeaseDialog vehicle={v} el={el} onEnded={load} />}
                                    {v.status === 'pending' && (
                                        <Button variant="ghost" size="sm" onClick={() => remove(v.id)} className="text-red-500 hover:text-red-600" data-testid={`leasing-vehicle-delete-${v.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!vehicles.length && (
                            <tr><td colSpan={8} className="py-6 text-center text-slate-400">{el ? 'Δεν υπάρχουν οχήματα' : 'No vehicles yet'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
